import { createContext, useContext, useEffect, useState } from 'react';
import { DataStore, Hub, syncExpression } from 'aws-amplify';
import { captureError } from '../common/sentry';
import { UserDetailsDataProps, userDetailsData } from '../datastore/userDetails';
import { SSCCsDataProps, ssccsData } from '../datastore/ssccs';
import { LocationDataProps, locationData } from '../datastore/locations';
import { PrinterDataProps, printersData } from '../datastore/printers';
import { PackageTypeDataProps, packageTypeData } from '../datastore/packageType';
import { QMReasonDataProps, qmReasonData } from '../datastore/qmReason';
import { QMIssueDataProps, qmIssueData } from '../datastore/qmIssue';
import { WorkflowStatusDataProps, workflowStatusData } from '../datastore/workflowStatus';
import { TripSheetsDataProps, tripSheetsData } from '../datastore/tripSheets';
import { RemoveItemSSCCHistoryDataProps, removeItemSSCCHistory } from '../datastore/removeItemSSCCHistory';
import { ReceiptEventPackageHistoryDataProps, receiptEventPackageHistory } from '../datastore/receiptEventHistory';
import { AdminActionHistoryDataProps, adminActionHistory } from '../datastore/adminActionHistory';
import { TransportBookingEventDataProps, transportBookingEventData } from '../datastore/transportBookingEvent';
import {
  AdminActionHistory,
  ReceiptEventPackageHistory,
  RemoveItemSSCCHistory,
  SSCC,
  TripSheet,
  TransportBookingEvent
} from '../models';
import dayjs from 'dayjs';
import { SsccStatus } from '../common';

export interface DatastoreContextProps {
  isDatastoreReady: boolean;
  isSyncing: boolean;
  syncProgress: {
    [key: string]: { new: number; updated: number; deleted: number };
  };
  userDetails: UserDetailsDataProps;
  ssccs: SSCCsDataProps;
  tripSheets: TripSheetsDataProps;
  locations: LocationDataProps;
  printers: PrinterDataProps;
  packageTypes: PackageTypeDataProps;
  qmReasons: QMReasonDataProps;
  qmIssues: QMIssueDataProps;
  removeItemSSCCHistories: RemoveItemSSCCHistoryDataProps;
  workflowStatuses: WorkflowStatusDataProps;
  receiptEventPackageHistories: ReceiptEventPackageHistoryDataProps;
  adminActionHistories: AdminActionHistoryDataProps;
  transportBookingEvents: TransportBookingEventDataProps;
}

interface DatastoreProviderProps {
  children?: React.ReactNode;
}

const defaultDatastoreConfiguration = {
  maxRecordsToSync: 50000,
  fullSyncInterval: +(process.env.REACT_APP_OVERRIDE_FULL_SYNC_MINUTES ?? 60 * 2),
  syncPageSize: 80
};

const activeHistoricalSsccStatuses = [
  SsccStatus.open,
  SsccStatus.staged,
  SsccStatus.manifested,
  SsccStatus.inTransit,
  SsccStatus.approaching
];

const DatastoreContext = createContext<DatastoreContextProps>({} as DatastoreContextProps);

const useDatastore = () => useContext(DatastoreContext);

const DatastoreProvider = ({ children }: DatastoreProviderProps) => {
  const [isDatastoreReady, setIsDatastoreReady] = useState(false);
  const [isSyncing, setIsSyncing] = useState(true);
  const [newRecordCounts, setNewRecordCounts] = useState<{
    [key: string]: { new: number; updated: number; deleted: number };
  }>({});

  const userDetails = userDetailsData();
  const ssccs = ssccsData();
  const tripSheets = tripSheetsData();
  const locations = locationData();
  const printers = printersData();
  const packageTypes = packageTypeData();
  const qmReasons = qmReasonData();
  const qmIssues = qmIssueData();
  const transportBookingEvents = transportBookingEventData();
  const workflowStatuses = workflowStatusData();
  const removeItemSSCCHistories = removeItemSSCCHistory();
  const receiptEventPackageHistories = receiptEventPackageHistory();
  const adminActionHistories = adminActionHistory();
  const days = +(process.env.SSCC_MANIFEST_FROM_LAST_X_DAYS ?? 60);
  const timePredicate = dayjs().subtract(days, 'day').toISOString();
  const futureDate = dayjs().add(5, 'year').toISOString();

  useEffect(() => {
    const listener = Hub.listen('datastore', async ({ payload: { event, data } }) => {
      console.info('datastore event', event, data);
      switch (event) {
        case 'ready':
          console.log(event);
          setIsDatastoreReady(true);
          setIsSyncing(false);
          break;
        case 'syncQueriesStarted':
          setIsSyncing(true);
          break;
        case 'syncQueriesReady':
          setIsSyncing(false);
          break;
        case 'outboxMutationFailed':
          console.error('DataStore outbox mutation failed:', data);
          captureError(
            new Error(
              `DataStore outbox mutation failed: ${data?.model?.name || 'unknown'} - ${data?.errorType || 'unknown error'}`
            ),
            'datastore-outbox-mutation-failed'
          );
          break;
        case 'networkStatus':
          if (data?.active === false) {
            console.warn('DataStore network status: offline');
          }
          break;
        case 'modelSynced':
          const {
            counts,
            model: { name: modelName }
          } = data;
          if (counts.new > 0 || counts.updated > 0 || counts.deleted > 0) {
            setNewRecordCounts((prev) => ({
              ...prev,
              [modelName]: counts
            }));
          }
          break;
      }
    });

    const syncExpressions = [
      syncExpression(TransportBookingEvent, () => {
        const transportBookingEventStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('TransportBookingEvent sync expression - filtering records after:', transportBookingEventStartDate);
        return (tbe) => tbe?.createdAt.gt(transportBookingEventStartDate);
      }),
      syncExpression(TripSheet, () => {
        const tripSheetStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('TripSheet sync expression - filtering records after:', tripSheetStartDate);
        return (ts) => ts?.createdAt.gt(tripSheetStartDate);
      }),
      syncExpression(SSCC, () => {
        return (sscc) =>
          sscc.or((sscc) => [
            sscc?.createdAt.ge(timePredicate),
            ...activeHistoricalSsccStatuses.map((status) =>
              sscc.and((sscc) => [sscc?.createdAt.lt(timePredicate), sscc?.status.eq(status)])
            )
          ]);
      }),
      syncExpression(AdminActionHistory, () => {
        const auditLogStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('AdminActionHistory sync expression - filtering records after:', auditLogStartDate);
        return (a) => a?.actionTime.gt(auditLogStartDate);
      }),
      syncExpression(ReceiptEventPackageHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      }),
      syncExpression(RemoveItemSSCCHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      })
    ];

    //Datastore documentation: https://docs.amplify.aws/lib/datastore/getting-started/q/platform/js/
    try {
      DataStore.configure({ ...defaultDatastoreConfiguration, syncExpressions });
      DataStore.start();
    } catch (error) {
      console.error('Failed to initialize DataStore:', error);
      captureError(error, 'datastore-initialize');
      setIsDatastoreReady(false);
      setIsSyncing(true);
    }

    return () => {
      listener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    isDatastoreReady: isDatastoreReady,
    isSyncing: isSyncing,
    syncProgress: newRecordCounts,
    userDetails,
    ssccs,
    tripSheets,
    locations,
    printers,
    packageTypes,
    qmIssues,
    qmReasons,
    workflowStatuses,
    removeItemSSCCHistories,
    receiptEventPackageHistories,
    adminActionHistories,
    transportBookingEvents
  };

  return <DatastoreContext.Provider value={value}>{children}</DatastoreContext.Provider>;
};

export { useDatastore, DatastoreProvider };
